// app/javascript/block-registry.ts
//
// Registers all @opensite/ui block components with @page-speed/blocks's
// renderer registry. Must be called once before the app renders.
//
// @opensite/ui block _type values correspond to the `id` field in @opensite/ui's
// block registry. Props are deserialized from the JSON __jsx format before
// being forwarded to the component.
import React from "react";
import { registerBlockRenderer } from "@page-speed/blocks";
import { getAllBlocks } from "@opensite/ui/registry";
import { DynamicIcon } from "@opensite/ui";
import type { BlockRendererProps } from "@page-speed/blocks/types";

// Native HTML tags that can appear as __jsx values in serialized payloads.
const HTML_TAGS = new Set([
  "div", "span", "p", "a", "button", "section", "article", "aside",
  "header", "footer", "main", "nav", "ul", "ol", "li",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "img", "picture", "figure", "figcaption", "br", "hr", "strong", "em",
]);

// Named React components that can appear as __jsx values.
const JSX_COMPONENTS: Record<string, React.ComponentType<any>> = {
  DynamicIcon,
};

// Recursively converts { __jsx, ...props } objects into React elements.
// Plain objects and arrays are walked so nested __jsx values are also resolved.
function deserializeJsx(value: unknown): unknown {
  if (value === null || value === undefined || typeof value !== "object") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(deserializeJsx);
  }

  const obj = value as Record<string, unknown>;

  if ("__jsx" in obj) {
    const { __jsx: tag, children, ...rest } = obj;
    const type = tag as string;

    const resolvedProps: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(rest)) {
      resolvedProps[k] = deserializeJsx(v);
    }

    let resolvedChildren: React.ReactNode[] | undefined;
    if (children !== undefined) {
      const c = deserializeJsx(children);
      resolvedChildren = (Array.isArray(c) ? c : [c]) as React.ReactNode[];
    }

    const component: string | React.ComponentType<any> =
      JSX_COMPONENTS[type] ?? (HTML_TAGS.has(type) ? type : "span");

    return resolvedChildren
      ? React.createElement(component, resolvedProps, ...resolvedChildren)
      : React.createElement(component, resolvedProps);
  }

  // Plain object — deserialize each value.
  const result: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    result[k] = deserializeJsx(v);
  }
  return result;
}

export function initOpenSiteRenderers(): void {
  const allBlocks = getAllBlocks();

  allBlocks.forEach(({ id, component: Component }: { id: string; component: React.ComponentType<any> }) => {
    registerBlockRenderer(id, ({ block }: BlockRendererProps) => {
      const rawProps = (block.blockProps ?? {}) as Record<string, unknown>;
      const props = deserializeJsx(rawProps) as Record<string, unknown>;
      return React.createElement(
        Component as React.ComponentType<Record<string, unknown>>,
        props
      );
    });
  });
}
